'use strict';

const { subtle, randomBytes } = require('./lib');
const { toB64, fromB64 } = require('./b64');

// The vault is the user's secrets, AES-256-GCM encrypted under the vault key
// derived from their password. The server only ever stores the opaque blob.

async function importAesKey(vaultKeyBytes) {
  return subtle().importKey('raw', vaultKeyBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/**
 * Encrypt a plaintext object into a portable vault blob.
 * @returns {{v:number, alg:string, iv:string, ct:string}} base64 fields.
 */
async function encryptVault(vaultKeyBytes, plaintextObj) {
  const key = await importAesKey(vaultKeyBytes);
  const iv = randomBytes(12); // 96-bit nonce per GCM best practice
  const pt = new TextEncoder().encode(JSON.stringify(plaintextObj));
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, key, pt);
  return { v: 1, alg: 'AES-256-GCM', iv: toB64(iv), ct: toB64(new Uint8Array(ct)) };
}

/**
 * Decrypt a vault blob. Throws if the vault key is wrong or the blob was tampered
 * with (GCM auth tag failure) — that thrown error IS the "wrong password" signal.
 */
async function decryptVault(vaultKeyBytes, blob) {
  const key = await importAesKey(vaultKeyBytes);
  const iv = fromB64(blob.iv);
  const ct = fromB64(blob.ct);
  const pt = await subtle().decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt));
}

module.exports = { encryptVault, decryptVault };
